import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { getCategoryConfig, saveCategoryConfig } from 'app/actions/BrowseActions';
import { TOP_LEVEL_CATEGORY_ID, TOP_LEVEL_CATEGORY_TITLE_BY_ID } from 'app/constants/AdminBrowseConstants';
import {
  makeCategoryConfigId,
  makeEmptyCategoryConfig,
  serializeEditedCategoryConfig,
  validateCategoryConfig
} from 'app/helpers/admin/browse/categories';
import CategoryConfigEditor from './CategoryConfigEditor';

const SUCCESS_MESSAGE_DURATION_MS = 3 * 1000;

const CategoryConfigEditorContainer = ({ backToUrl, currentUsername, languageKey, subdomain, topLevelCategoryId }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    categoryConfig: null,
    error: '',
    isFetching: false,
    successMessage: ''
  });

  const categoryConfigId = makeCategoryConfigId(subdomain, languageKey, topLevelCategoryId);
  const categoryTitle = TOP_LEVEL_CATEGORY_TITLE_BY_ID[topLevelCategoryId];

  const { categoryConfig, error, isFetching, successMessage } = state;

  const handleSaveCategoryConfig = editedCategoryConfig => {
    const validationErrors = validateCategoryConfig(editedCategoryConfig);
    const hasErrors = validationErrors.length > 0;

    if (hasErrors) {
      setState(prevState => ({ ...prevState, error: validationErrors.join('; ') }));
    } else {
      setState(prevState => ({ ...prevState, categoryConfig: null, error: '', isFetching: true }));

      dispatch(saveCategoryConfig(serializeEditedCategoryConfig(editedCategoryConfig, currentUsername)))
        .then(savedCategoryConfig => {
          setState(prevState => {
            return {
              ...prevState,
              categoryConfig:
                savedCategoryConfig || makeEmptyCategoryConfig(subdomain, languageKey, topLevelCategoryId),
              successMessage: `Successfully saved ${categoryTitle}, ${languageKey} category config`
            };
          });
        })
        .catch(() => {
          setState(prevState => ({ ...prevState, error: 'Failed to save. 😞' }));
        })
        .finally(() => {
          setState(prevState => ({ ...prevState, isFetching: false }));
        });
    }
  };

  useEffect(() => {
    let isMounted = true;

    setState(prevState => ({ ...prevState, error: '', isFetching: true }));
    dispatch(getCategoryConfig(categoryConfigId))
      .then(fetchedCategoryConfig => {
        if (!isMounted) {
          return;
        }

        setState(prevState => {
          return {
            ...prevState,
            categoryConfig: fetchedCategoryConfig || makeEmptyCategoryConfig(subdomain, languageKey, topLevelCategoryId)
          };
        });
      })
      .catch(() => {
        setState(prevState => ({ ...prevState, error: 'Something went wrong. 😞' }));
      })
      .finally(() => {
        setState(prevState => ({ ...prevState, isFetching: false }));
      });

    return () => {
      isMounted = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryConfigId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setState(prevState => ({ ...prevState, successMessage: '' }));
    }, SUCCESS_MESSAGE_DURATION_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [successMessage]);

  return (
    <div>
      <Link dataKey="back-to-languages" to={backToUrl}>
        Back to all category configs for {categoryTitle}
      </Link>
      <h2>
        [{languageKey}] {categoryTitle}
      </h2>

      {successMessage && <p style={{ background: 'green', color: 'white', padding: '16px' }}>{successMessage}</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {isFetching && <p>Loading...</p>}
      {categoryConfig && (
        <CategoryConfigEditor
          categoryConfig={categoryConfig}
          save={handleSaveCategoryConfig}
          topLevelCategoryId={topLevelCategoryId}
        />
      )}
    </div>
  );
};

CategoryConfigEditorContainer.propTypes = {
  backToUrl: PropTypes.string.isRequired,
  currentUsername: PropTypes.string.isRequired,
  languageKey: PropTypes.string.isRequired, // TODO: do we know all possible values?
  subdomain: PropTypes.string.isRequired,
  topLevelCategoryId: PropTypes.oneOf(Object.values(TOP_LEVEL_CATEGORY_ID)).isRequired
};

export default CategoryConfigEditorContainer;
